import React, { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

function norm(s) {
  return (s ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateFR(raw) {
  if (!raw) return "—";
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("fr-FR");
}

function formatHistoryDates(historyArr, lastAt) {
  const values = [];
  if (Array.isArray(historyArr)) values.push(...historyArr);
  if (lastAt) values.push(lastAt);

  const map = new Map();
  for (const v of values) {
    if (!v) continue;
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) continue;
    const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    map.set(d0.getTime(), d0);
  }

  const dates = Array.from(map.values()).sort((a, b) => a.getTime() - b.getTime());
  if (dates.length === 0) return "—";
  return dates.map((d) => d.toLocaleDateString("fr-FR")).join(" | ");
}

const ADMIN_KEY_STORAGE = "siao_admin_key";

function loadAdminKey() {
  try {
    return localStorage.getItem(ADMIN_KEY_STORAGE) || "";
  } catch {
    return "";
  }
}

function saveAdminKeyToStorage(key) {
  try {
    localStorage.setItem(ADMIN_KEY_STORAGE, key);
  } catch {
    // ignore
  }
}

function clearAdminKeyFromStorage() {
  try {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
  } catch {
    // ignore
  }
}

function dateToInputValue(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

async function readErrorBody(res) {
  try {
    const txt = await res.text();
    return txt || "";
  } catch {
    return "";
  }
}

export default function OperateurDemandeDetail({ demande, onBack, viewerName }) {
  const [statutDemande, setStatutDemande] = useState("");
  const [commentaires, setCommentaires] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [localDemande, setLocalDemande] = useState(null);

  // ✅ prioritaire (autosave)
  const [prioritaire, setPrioritaire] = useState(false);
  const [prioSaving, setPrioSaving] = useState(false);
  const [prioErr, setPrioErr] = useState("");

  // ✅ alerte (autosave)
  const [alerte, setAlerte] = useState(false);
  const [alerteSaving, setAlerteSaving] = useState(false);
  const [alerteErr, setAlerteErr] = useState("");

  // Admin UI
  const [adminKeyStored, setAdminKeyStored] = useState(() => loadAdminKey());
  const [adminKeyInput, setAdminKeyInput] = useState("");
  const [adminMsg, setAdminMsg] = useState("");
  const [adminErr, setAdminErr] = useState("");
  const [adminOperateur, setAdminOperateur] = useState("");
  const [adminDates, setAdminDates] = useState({});
  const [adminDatesInitial, setAdminDatesInitial] = useState({});
  const [adminSaving, setAdminSaving] = useState(false);

  // Retour accueil manuel
  const [returning, setReturning] = useState(false);
  const [returnErr, setReturnErr] = useState("");
  const [returnMsg, setReturnMsg] = useState("");

  const d = localDemande || demande || null;

  const isElissaViewer = norm(viewerName) === norm("ELISSA");
  const isAdminMode = !!adminKeyStored;

  const adminHeaders = useMemo(() => {
    return isAdminMode ? { "x-admin-key": adminKeyStored } : {};
  }, [isAdminMode, adminKeyStored]);

  const ADMIN_DATE_FIELDS = useMemo(
    () => [
      { key: "date_premiere_transmission", label: "Date de transmission SIAO" },
      { key: "assigned_at", label: "Date d’attribution (opérateur)" },
      { key: "eaci_at", label: "Dernier EACI" },
      { key: "orientees_at", label: "Dernières demandes orientées" },
      { key: "annulees_at", label: "Dernières demandes annulées" },
      { key: "liste_attente_at", label: "Dernière liste d’attente" },
      { key: "flechage_at", label: "Dernier fléchage" },
      { key: "retour_cds_at", label: "Dernier retour CDS" },
      { key: "closed_at", label: "Clôture dossier" },
      { key: "retour_eaci_at", label: "Retour EACI (date)" },
    ],
    []
  );

  useEffect(() => {
    if (!demande) return;
    setLocalDemande(demande);
    setStatutDemande(demande.statut_demande || "");
    setCommentaires(demande.commentaires || demande.commentaire || "");
    setPrioritaire(!!demande.prioritaire);
    setAlerte(!!demande.alerte);
    setPrioErr("");
    setAlerteErr("");
  }, [demande]);

  // sync après update
  useEffect(() => {
    if (!d) return;
    setPrioritaire(!!d.prioritaire);
  }, [d?.prioritaire]);

  useEffect(() => {
    if (!d) return;
    setAlerte(!!d.alerte);
  }, [d?.alerte]);

  const initAdminOperateur = useMemo(() => {
    return ((d?.operateur ?? "") + "").trim();
  }, [d?.operateur]);

  const initAdminDates = useMemo(() => {
    const next = {};
    for (const f of ADMIN_DATE_FIELDS) {
      next[f.key] = dateToInputValue(d?.[f.key]);
    }
    return next;
  }, [ADMIN_DATE_FIELDS, d]);

  useEffect(() => {
    setAdminOperateur(initAdminOperateur);
    setAdminDates(initAdminDates);
    setAdminDatesInitial(initAdminDates);
  }, [initAdminOperateur, initAdminDates]);

  if (!demande) return <p>Aucune donnée disponible</p>;

  const statusNorm = norm(d?.statut_demande);
  const isDemandeTransmise = !statusNorm || statusNorm === norm("demandes transmises");
  const hasOperateur = !!String(d?.operateur || "").trim();
  const isOwner = hasOperateur && norm(d?.operateur) === norm(viewerName);
  const canManualReturn = isDemandeTransmise && hasOperateur && (isOwner || isAdminMode);

  // ✅ autosave flags
  const saveFlagOnly = async (flagKey, nextValue, setSavingState, setErrState, rollback) => {
    setErrState("");
    setSavingState(true);

    try {
      const res = await fetch(`${API}/api/demandes/${demande._id}/details`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          [flagKey]: !!nextValue,
          modified_by: viewerName || "",
        }),
      });

      if (!res.ok) {
        const txt = await readErrorBody(res);
        throw new Error(`${res.status} ${txt}`);
      }

      const updated = await res.json();
      setLocalDemande(updated);
    } catch (e) {
      console.error(e);
      rollback();
      setErrState("Impossible d’enregistrer.");
    } finally {
      setSavingState(false);
    }
  };

  const saveDetails = async () => {
    setError("");
    setOkMsg("");
    setSaving(true);

    try {
      const isFlechage = norm(statutDemande) === norm("flechage");
      const isRetourCDS = norm(statutDemande) === norm("retour decision cds");

      let statutToSave = statutDemande;
      let flechageToSave = "";

      if (isFlechage) {
        statutToSave = "Fléchage";
        flechageToSave = "ELISSA";
      } else if (isRetourCDS) {
        statutToSave = "Retour décision CDS";
        flechageToSave = "";
      } else {
        statutToSave = statutDemande;
        flechageToSave = "";
      }

      const res = await fetch(`${API}/api/demandes/${demande._id}/details`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          statut_demande: statutToSave,
          commentaires: commentaires,
          flechage: flechageToSave,
          prioritaire: prioritaire,
          alerte: alerte,
          modified_by: viewerName || "",
        }),
      });

      if (!res.ok) {
        const txt = await readErrorBody(res);
        throw new Error(`${res.status} ${txt}`);
      }

      const updated = await res.json();
      setLocalDemande(updated);

      setOkMsg("✅ Enregistré");
      setTimeout(() => setOkMsg(""), 1500);
    } catch (e) {
      console.error(e);
      setError("Impossible d’enregistrer.");
    } finally {
      setSaving(false);
    }
  };

  const activateAdmin = () => {
    setAdminErr("");
    setAdminMsg("");
    const key = (adminKeyInput || "").toString().trim();
    if (!key) {
      setAdminErr("Clé admin manquante.");
      return;
    }
    saveAdminKeyToStorage(key);
    setAdminKeyStored(key);
    setAdminKeyInput("");
    setAdminMsg("✅ Mode admin activé.");
    setTimeout(() => setAdminMsg(""), 1500);
  };

  const deactivateAdmin = () => {
    clearAdminKeyFromStorage();
    setAdminKeyStored("");
    setAdminKeyInput("");
    setAdminErr("");
    setAdminMsg("Mode admin désactivé.");
    setTimeout(() => setAdminMsg(""), 1500);
  };

  const saveAdminOperateur = async () => {
    setAdminErr("");
    setAdminMsg("");

    if (!isAdminMode) {
      setAdminErr("Mode admin non activé.");
      return;
    }

    const nextOp = (adminOperateur || "").toString().trim().toUpperCase();
    const curOp = (d?.operateur || "").toString().trim().toUpperCase();

    if (!nextOp) {
      setAdminErr("Opérateur manquant.");
      return;
    }
    if (nextOp === curOp) {
      setAdminErr("Aucun changement (opérateur identique).");
      return;
    }

    setAdminSaving(true);
    try {
      const res = await fetch(`${API}/api/demandes/${demande._id}/operateur`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...adminHeaders },
        body: JSON.stringify({ operateur: nextOp }),
      });

      if (!res.ok) {
        const txt = await readErrorBody(res);
        throw new Error(`${res.status} ${txt}`);
      }

      const updated = await res.json();
      setLocalDemande(updated);

      setAdminMsg("✅ Opérateur modifié.");
      setTimeout(() => setAdminMsg(""), 1500);
    } catch (e) {
      console.error(e);
      setAdminErr("Impossible de modifier l’opérateur (clé admin).");
    } finally {
      setAdminSaving(false);
    }
  };

  const saveAdminDates = async () => {
    setAdminErr("");
    setAdminMsg("");

    if (!isAdminMode) {
      setAdminErr("Mode admin non activé.");
      return;
    }

    const patch = {};
    for (const f of ADMIN_DATE_FIELDS) {
      const k = f.key;
      const cur = adminDatesInitial?.[k] ?? "";
      const next = adminDates?.[k] ?? "";
      if (next !== cur) patch[k] = next ? next : null;
    }

    if (Object.keys(patch).length === 0) {
      setAdminErr("Aucune date modifiée.");
      return;
    }

    setAdminSaving(true);
    try {
      const res = await fetch(`${API}/api/demandes/${demande._id}/admin/dates`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...adminHeaders },
        body: JSON.stringify(patch),
      });

      if (!res.ok) {
        const txt = await readErrorBody(res);
        throw new Error(`${res.status} ${txt}`);
      }

      const updated = await res.json();
      setLocalDemande(updated);

      setAdminMsg("✅ Dates mises à jour.");
      setTimeout(() => setAdminMsg(""), 1500);
    } catch (e) {
      console.error(e);
      setAdminErr("Impossible de modifier les dates (clé admin).");
    } finally {
      setAdminSaving(false);
    }
  };

  const resetAdminDatesForm = () => {
    setAdminErr("");
    setAdminMsg("");
    setAdminDates(adminDatesInitial);
  };

  const manualReturnToAccueil = async () => {
    setReturnErr("");
    setReturnMsg("");

    if (!canManualReturn) {
      setReturnErr("Non autorisé.");
      return;
    }

    const ok = window.confirm(
      "Renvoyer cette demande dans l’accueil ?\n\nCela annulera l’opérateur et la date d’attribution."
    );
    if (!ok) return;

    setReturning(true);
    try {
      const res = await fetch(`${API}/api/demandes/${demande._id}/return-accueil`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...adminHeaders },
        body: JSON.stringify({ by: viewerName || "" }),
      });

      if (!res.ok) {
        const txt = await readErrorBody(res);
        throw new Error(`${res.status} ${txt}`);
      }

      const updated = await res.json();
      setLocalDemande(updated);

      setReturnMsg("✅ Demande renvoyée dans l’accueil.");
      setTimeout(() => {
        if (typeof onBack === "function") onBack();
      }, 400);
    } catch (e) {
      console.error(e);
      setReturnErr("Impossible de renvoyer la demande (clé / droits).");
    } finally {
      setReturning(false);
    }
  };

  return (
    <div className="detail-card" style={{ position: "relative" }}>
      {d?.last_modified_at ? (
        <div
          style={{
            position: "absolute",
            top: 12,
            right: 14,
            fontSize: 12,
            opacity: 0.75,
            textAlign: "right",
          }}
        >
          Dernière modification : {formatDate(d.last_modified_at)}
          {d.last_modified_by ? ` — ${d.last_modified_by}` : ""}
        </div>
      ) : null}

      {/* ✅ ALERTE */}
      {d?.alerte ? (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid #fecaca",
            background: "#fee2e2",
            color: "#991b1b",
            fontWeight: 950,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          ❗ ALERTE
          {d?.alerte_at ? (
            <span style={{ fontWeight: 700, opacity: 0.85 }}>
              (depuis {formatDateFR(d.alerte_at)})
            </span>
          ) : null}
        </div>
      ) : null}

      {/* ✅ PRIORITAIRE */}
      {d?.prioritaire ? (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#991b1b",
            fontWeight: 950,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          ⚠️ DOSSIER PRIORITAIRE
          {d?.prioritaire_at ? (
            <span style={{ fontWeight: 700, opacity: 0.85 }}>
              (depuis {formatDateFR(d.prioritaire_at)})
            </span>
          ) : null}
        </div>
      ) : null}

      <h2 style={{ marginTop: 0 }}>Détails de la demande</h2>

      <div className="detail-grid">
        <div className="detail-field">
          <div className="k">Nom</div>
          <div className="v">{demande.nom_naissance || "—"}</div>
        </div>

        <div className="detail-field">
          <div className="k">Prénom</div>
          <div className="v">{demande.prenom || "—"}</div>
        </div>

        <div className="detail-field">
          <div className="k">Date de naissance</div>
          <div className="v">{formatDateFR(demande.date_naissance)}</div>
        </div>

        <div className="detail-field">
          <div className="k">Nb personnes</div>
          <div className="v">{demande.nb_pers_a_heberger ?? "—"}</div>
        </div>

        <div className="detail-field">
          <div className="k">Travailleur social</div>
          <div className="v">{demande.nom_prenom_ts1 || "—"}</div>
        </div>

        <div className="detail-field">
          <div className="k">Email TS</div>
          <div className="v">{demande.courriel_ts1 || "—"}</div>
        </div>

        <div className="detail-field">
          <div className="k">Transmission SIAO</div>
          <div className="v">
            {d?.date_premiere_transmission || demande.date_premiere_transmission || "—"}
          </div>
        </div>

        <div className="detail-field">
          <div className="k">Opérateur</div>
          <div className="v">{d?.operateur || "—"}</div>
        </div>
      </div>

      {/* ✅ Checkbox PRIORITAIRE (autosave) */}
      <div style={{ marginTop: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900 }}>
          <input
            type="checkbox"
            checked={!!prioritaire}
            onChange={(e) => {
              const next = e.target.checked;
              const prev = prioritaire;
              setPrioritaire(next);
              saveFlagOnly(
                "prioritaire",
                next,
                setPrioSaving,
                setPrioErr,
                () => setPrioritaire(prev)
              );
            }}
            disabled={saving || prioSaving}
            style={{ width: 18, height: 18 }}
          />
          Dossier prioritaire
          {prioSaving ? <span style={{ fontWeight: 800, opacity: 0.7 }}>(sauvegarde…)</span> : null}
        </label>
        {prioErr ? <div style={{ color: "red", marginTop: 6, fontWeight: 800 }}>{prioErr}</div> : null}
      </div>

      {/* ✅ Checkbox ALERTE (autosave) */}
      <div style={{ marginTop: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900 }}>
          <input
            type="checkbox"
            checked={!!alerte}
            onChange={(e) => {
              const next = e.target.checked;
              const prev = alerte;
              setAlerte(next);
              saveFlagOnly("alerte", next, setAlerteSaving, setAlerteErr, () => setAlerte(prev));
            }}
            disabled={saving || alerteSaving}
            style={{ width: 18, height: 18 }}
          />
          Alerte
          {alerteSaving ? (
            <span style={{ fontWeight: 800, opacity: 0.7 }}>(sauvegarde…)</span>
          ) : null}
        </label>
        {alerteErr ? (
          <div style={{ color: "red", marginTop: 6, fontWeight: 800 }}>{alerteErr}</div>
        ) : null}
      </div>

      {/* Historique */}
      <div style={{ marginTop: 18 }}>
        <div style={{ fontWeight: 950, marginBottom: 8 }}>Historique des statuts</div>

        <div className="detail-grid">
          <div className="detail-field">
            <div className="k">Historique EACI</div>
            <div className="v">{formatHistoryDates(d?.eaci_history, d?.eaci_at)}</div>
          </div>

          <div className="detail-field">
            <div className="k">Historique demandes orientées</div>
            <div className="v">{formatHistoryDates(d?.orientees_history, d?.orientees_at)}</div>
          </div>

          <div className="detail-field">
            <div className="k">Historique demandes annulées</div>
            <div className="v">{formatHistoryDates(d?.annulees_history, d?.annulees_at)}</div>
          </div>

          <div className="detail-field">
            <div className="k">Historique demandes transférées</div>
            <div className="v">{formatHistoryDates(d?.transferees_history, d?.transferees_at)}</div>
          </div>

          <div className="detail-field">
            <div className="k">Historique Sans Affectation</div>
            <div className="v">
              {formatHistoryDates(d?.sans_affectation_history, d?.sans_affectation_at)}
            </div>
          </div>

          <div className="detail-field">
            <div className="k">Historique liste d’attente</div>
            <div className="v">{formatHistoryDates(d?.liste_attente_history, d?.liste_attente_at)}</div>
          </div>

          <div className="detail-field">
            <div className="k">Historique fléchage</div>
            <div className="v">{formatHistoryDates(d?.flechage_history, d?.flechage_at)}</div>
          </div>

          <div className="detail-field">
            <div className="k">Historique retour CDS</div>
            <div className="v">{formatHistoryDates(d?.retour_cds_history, d?.retour_cds_at)}</div>
          </div>
        </div>
      </div>

      {canManualReturn ? (
        <div
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 12,
            border: "1px solid #eee",
            background: "#fff7ed",
          }}
        >
          <div style={{ fontWeight: 950, marginBottom: 8 }}>Demande transmise — retour à l’accueil</div>

          <button
            onClick={manualReturnToAccueil}
            disabled={returning}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "none",
              cursor: "pointer",
              backgroundColor: "#b91c1c",
              color: "white",
              fontWeight: 900,
            }}
          >
            {returning ? "..." : "Renvoyer la demande dans l’accueil"}
          </button>

          {returnErr ? <div style={{ color: "red", marginTop: 10 }}>{returnErr}</div> : null}
          {returnMsg ? <div style={{ color: "green", marginTop: 10 }}>{returnMsg}</div> : null}
        </div>
      ) : null}

      <div style={{ marginTop: 18 }}>
        <div style={{ fontWeight: 950, marginBottom: 8 }}>Statut de la demande</div>

        <select
          value={statutDemande}
          onChange={(e) => setStatutDemande(e.target.value)}
          disabled={saving}
          style={{ padding: 10, width: 320 }}
        >
          <option value="">-- Choisir --</option>
          <option value="EACI">EACI</option>
          <option value="Demandes orientées">Demandes orientées</option>
          <option value="Demandes annulées">Demandes annulées</option>
          <option value="Demandes transférées">Demandes transférées</option>
          <option value="Sans Affectation">Sans Affectation</option>
          <option value="Liste d'attente">Liste d'attente</option>
          <option value="Flechage">Flechage</option>
          {isElissaViewer ? <option value="Retour décision CDS">Retour décision CDS</option> : null}
        </select>
      </div>

      <div style={{ marginTop: 18 }}>
        <div style={{ fontWeight: 950, marginBottom: 8 }}>Commentaires</div>

        <textarea
          value={commentaires}
          onChange={(e) => setCommentaires(e.target.value)}
          disabled={saving}
          placeholder="Écrire un commentaire…"
          style={{
            width: "100%",
            maxWidth: 900,
            height: 140,
            padding: 10,
            borderRadius: 10,
            border: "1px solid #ddd",
            resize: "vertical",
          }}
        />
      </div>

      {error ? <div style={{ color: "red", marginTop: 10 }}>{error}</div> : null}
      {okMsg ? <div style={{ color: "green", marginTop: 10 }}>{okMsg}</div> : null}

      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <button
          onClick={saveDetails}
          disabled={saving}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "none",
            cursor: "pointer",
            backgroundColor: "#111827",
            color: "white",
            fontWeight: 900,
          }}
        >
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>

        <button
          onClick={onBack}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #ddd",
            cursor: "pointer",
            background: "white",
            fontWeight: 900,
          }}
        >
          Retour
        </button>
      </div>

      {/* Mode admin (inchangé) */}
      <div
        style={{
          marginTop: 18,
          padding: 14,
          borderRadius: 12,
          border: "1px solid #eee",
          background: "#fafafa",
        }}
      >
        <div style={{ fontWeight: 950, marginBottom: 8 }}>
          Mode admin{" "}
          {isAdminMode ? (
            <span style={{ marginLeft: 10, color: "green", fontWeight: 900 }}>• activé</span>
          ) : (
            <span style={{ marginLeft: 10, color: "#b45309", fontWeight: 900 }}>• inactif</span>
          )}
        </div>

        {!isAdminMode ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="password"
              value={adminKeyInput}
              onChange={(e) => setAdminKeyInput(e.target.value)}
              placeholder="Clé admin"
              style={{
                padding: 10,
                borderRadius: 10,
                border: "1px solid #ddd",
                width: 320,
              }}
            />
            <button
              onClick={activateAdmin}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                backgroundColor: "#111827",
                color: "white",
                fontWeight: 900,
              }}
            >
              Activer
            </button>
          </div>
        ) : (
          <button
            onClick={deactivateAdmin}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #ddd",
              cursor: "pointer",
              background: "white",
              fontWeight: 900,
            }}
          >
            Désactiver
          </button>
        )}

        {adminErr ? <div style={{ color: "red", marginTop: 10 }}>{adminErr}</div> : null}
        {adminMsg ? <div style={{ color: "green", marginTop: 10 }}>{adminMsg}</div> : null}

        {isAdminMode ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 950, marginBottom: 8 }}>Actions admin</div>

            <div
              style={{
                padding: 12,
                borderRadius: 12,
                border: "1px solid #eee",
                background: "white",
                marginBottom: 12,
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Réaffecter l’opérateur</div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  value={adminOperateur}
                  onChange={(e) => setAdminOperateur(e.target.value)}
                  placeholder="Nom opérateur (ex: AMEL)"
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #ddd",
                    width: 320,
                  }}
                  disabled={adminSaving}
                />

                <button
                  onClick={saveAdminOperateur}
                  disabled={adminSaving}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "none",
                    cursor: "pointer",
                    backgroundColor: "#111827",
                    color: "white",
                    fontWeight: 900,
                  }}
                >
                  {adminSaving ? "..." : "Enregistrer opérateur"}
                </button>
              </div>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 12,
                border: "1px solid #eee",
                background: "white",
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Modifier des dates</div>

              <div className="detail-grid">
                {ADMIN_DATE_FIELDS.map((f) => (
                  <div className="detail-field" key={f.key}>
                    <div className="k">{f.label}</div>
                    <div className="v">
                      <input
                        type="date"
                        value={adminDates?.[f.key] ?? ""}
                        onChange={(e) =>
                          setAdminDates((prev) => ({ ...(prev || {}), [f.key]: e.target.value }))
                        }
                        disabled={adminSaving}
                        style={{
                          padding: 8,
                          borderRadius: 10,
                          border: "1px solid #ddd",
                          width: 200,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <button
                  onClick={saveAdminDates}
                  disabled={adminSaving}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "none",
                    cursor: "pointer",
                    backgroundColor: "#111827",
                    color: "white",
                    fontWeight: 900,
                  }}
                >
                  {adminSaving ? "..." : "Enregistrer dates"}
                </button>

                <button
                  onClick={resetAdminDatesForm}
                  disabled={adminSaving}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid #ddd",
                    cursor: "pointer",
                    background: "white",
                    fontWeight: 900,
                  }}
                >
                  Annuler les changements
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
